/**
 * Como é a mesa (docs/spec/explore-rooms.md, "Como é a mesa"): a língua, a frequência, o visual, a
 * cobrança e o "Sobre a mesa", que o mestre preenche no Perfil da mesa e o Explorar mostra e filtra.
 * Fica fora de `rooms.ts` porque o `RoomSchema` (domain.ts) também usa, e `rooms.ts` importa domain.ts.
 */
import { z } from 'zod';

/** Com que frequência a mesa joga. */
export const ROOM_FREQUENCIES = ['several-weekly', 'weekly', 'biweekly', 'monthly', 'irregular', 'one-shot'] as const;
export type RoomFrequency = (typeof ROOM_FREQUENCIES)[number];

/** Quanto a mesa usa de visual: mapas e tokens, algumas imagens ou só a imaginação. */
export const ROOM_VISUALS = ['maps', 'some-images', 'theater-of-mind'] as const;
export type RoomVisuals = (typeof ROOM_VISUALS)[number];

/** A língua da sessão: códigos ISO 639-1. O nome aparece pelo `Intl.DisplayNames` da interface. */
export const ROOM_LANGUAGES = ['pt', 'en', 'es', 'fr', 'de', 'it', 'nl', 'pl', 'ru', 'uk', 'sv', 'tr', 'ar', 'ja', 'ko', 'zh'] as const;
export type RoomLanguage = (typeof ROOM_LANGUAGES)[number];

export const ROOM_ABOUT_MAX = 4000;
export const ROOM_PRICE_MAX = 80;

// vazio = o mestre não informou
export const RoomLanguageSchema = z.union([z.enum(ROOM_LANGUAGES), z.literal('')]);
export const RoomFrequencySchema = z.union([z.enum(ROOM_FREQUENCIES), z.literal('')]);
export const RoomVisualsSchema = z.union([z.enum(ROOM_VISUALS), z.literal('')]);

/**
 * Os detalhes gravados (o JSON de `room_details`). Cada campo se recupera sozinho: um valor que saiu da
 * lista ou passou do limite vira vazio, sem derrubar a mesa.
 */
export const RoomDetailsSchema = z.object({
  language: RoomLanguageSchema.catch(''),
  frequency: RoomFrequencySchema.catch(''),
  visuals: RoomVisualsSchema.catch(''),
  /** o mestre cobra para mestrar */
  payToPlay: z.boolean().catch(false),
  /** o valor, em texto ("R$ 20 por sessão"); só vale com `payToPlay` */
  price: z.string().max(ROOM_PRICE_MAX).catch(''),
  /** "Sobre a mesa", em Talemark */
  about: z.string().max(ROOM_ABOUT_MAX).catch(''),
});
export type RoomDetails = z.infer<typeof RoomDetailsSchema>;

export const EMPTY_ROOM_DETAILS: RoomDetails = { language: '', frequency: '', visuals: '', payToPlay: false, price: '', about: '' };
export const ROOM_DETAIL_KEYS = Object.keys(EMPTY_ROOM_DETAILS) as (keyof RoomDetails)[];
